interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private history: Map<string, number[]> = new Map();
  private maxHistorySize: number = 100;
  private enabled: boolean = false;

  enable() {
    this.enabled = true;
  }

  disable() {
    this.enabled = false;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  start(name: string) {
    if (!this.enabled) return;
    this.metrics.set(name, {
      name,
      startTime: performance.now(),
    });
  }

  end(name: string): number | undefined {
    if (!this.enabled) return undefined;

    const metric = this.metrics.get(name);
    if (!metric) return undefined;

    metric.endTime = performance.now();
    metric.duration = metric.endTime - metric.startTime;

    if (!this.history.has(name)) {
      this.history.set(name, []);
    }
    const historyArr = this.history.get(name)!;
    historyArr.push(metric.duration);
    if (historyArr.length > this.maxHistorySize) {
      historyArr.shift();
    }

    this.metrics.delete(name);
    return metric.duration;
  }

  getAverageTime(name: string): number {
    const historyArr = this.history.get(name);
    if (!historyArr || historyArr.length === 0) return 0;
    return historyArr.reduce((sum, t) => sum + t, 0) / historyArr.length;
  }

  getMaxTime(name: string): number {
    const historyArr = this.history.get(name);
    if (!historyArr || historyArr.length === 0) return 0;
    return Math.max(...historyArr);
  }

  getMinTime(name: string): number {
    const historyArr = this.history.get(name);
    if (!historyArr || historyArr.length === 0) return 0;
    return Math.min(...historyArr);
  }

  getReport(): Record<string, { avg: number; min: number; max: number; count: number }> {
    const report: Record<string, { avg: number; min: number; max: number; count: number }> = {};
    for (const [name, historyArr] of this.history) {
      if (historyArr.length === 0) continue;
      report[name] = {
        avg: this.getAverageTime(name),
        min: this.getMinTime(name),
        max: this.getMaxTime(name),
        count: historyArr.length,
      };
    }
    return report;
  }

  clear() {
    this.metrics.clear();
    this.history.clear();
  }
}

export const performanceMonitor = new PerformanceMonitor();
